import { useRouter } from "next/navigation";
import { Bell, Compass, Crosshair, ScanSearch, Search } from "lucide-react";
import { SpotToast } from "@/components/SpotToast";
import { type GenieTool } from "../registries/tool-registry";
import {
  elementLabel,
  getSnapshotElement,
  hasLabel,
  takeSnapshot,
} from "../dom-snapshot";
import { type GeniePage, openablePages } from "../pages";
import { showSpotlight } from "../spotlight";
import { useGenieTools } from "../use-genie-tools";

// A new page mounts over a few frames after the route changes; reading it
// before the DOM goes quiet misses parts of it.
function waitForPageToSettle(quietMs = 150, maxMs = 1500): Promise<void> {
  return new Promise((resolve) => {
    let quiet = window.setTimeout(done, quietMs);
    const cap = window.setTimeout(done, maxMs);
    const observer = new MutationObserver(() => {
      window.clearTimeout(quiet);
      quiet = window.setTimeout(done, quietMs);
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
    });
    function done() {
      observer.disconnect();
      window.clearTimeout(quiet);
      window.clearTimeout(cap);
      resolve();
    }
  });
}

// Returns once the new page is the one on screen: a read of the page before
// then still sees the page the user left.
export async function navigateAndSettle(
  push: (path: string) => void,
  path: string,
  maxMs = 5000
): Promise<{ navigated: string }> {
  const start = window.location.pathname;
  const target = new URL(path, window.location.origin).pathname;
  // A highlight belongs to the page it was made on.
  showSpotlight(null);
  push(path);
  const deadline = Date.now() + maxMs;
  // A page may redirect elsewhere (no permission): any move away counts.
  while (
    window.location.pathname === start &&
    window.location.pathname !== target &&
    Date.now() < deadline
  ) {
    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }
  await waitForPageToSettle(300, 3000);
  return { navigated: path };
}

// Only the app's own pages, as the catalog lists them: the approval card names
// the page Genie wrote, so the path itself must not lead anywhere else.
export function isCatalogPath(path: string, pages: GeniePage[]): boolean {
  // "//host" and a backslash ("/\\host" in a string) leave the origin.
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) {
    return false;
  }
  const pathname = path.split(/[?#]/)[0];
  return pages.some((page) => {
    const pattern = page.path
      .split(/[?#]/)[0]
      .split("/")
      .map((part) =>
        part.startsWith(":")
          ? "[^/]+"
          : part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      )
      .join("/");
    return new RegExp(`^${pattern}$`).test(pathname);
  });
}

// Why navigate_to refuses a path, or null when it can go there.
export function navigationError(
  path: string,
  pages: GeniePage[]
): string | null {
  if (/(^|\/):/.test(path.split(/[?#]/)[0]) || /=:/.test(path)) {
    return `"${path}" still has a placeholder: put the record's id in its place.`;
  }
  if (!isCatalogPath(path, pages)) {
    return `"${path}" is not a page in this app. Use a path from search_pages.`;
  }
  return null;
}

// Points at an element without touching it: no click, nothing changes.
export function highlightElement(
  elementId: string,
  label: string,
  note: string
): { error: string } | { highlighted: string } {
  const el = getSnapshotElement(elementId);
  if (!el) {
    return {
      error: `Element "${elementId}" is not on the page anymore. Call get_page_elements again.`,
    };
  }
  if (!hasLabel(el, label)) {
    return {
      error: `Element "${elementId}" is "${elementLabel(el)}", not "${label}". Nothing was highlighted.`,
    };
  }
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  showSpotlight({ target: el, note, pathname: window.location.pathname });
  return { highlighted: label };
}

export function usePlatformTools(groupId: string, editorV2Enabled: boolean) {
  const router = useRouter();

  useGenieTools(() => {
    const pages = openablePages(editorV2Enabled);
    const tools: GenieTool[] = [
      {
        name: "navigate_to",
        description:
          "Navigate to a page in the platform. Use search_pages first to find the correct path. Do NOT guess URLs.",
        // Moves the user off the page they are on, discarding whatever is in
        // flight there. The user confirms the move.
        requires_approval: true,
        parameters: {
          type: "object",
          properties: {
            path: {
              type: "string",
              description: "The exact path to navigate to (from search_pages)",
            },
            page: {
              type: "string",
              description:
                "The page's name as the user knows it, shown on the approval card (e.g. 'Leads table', 'API keys')",
            },
          },
          required: ["path", "page"],
        },
        execute: async (input) => {
          let path = input.path as string;
          path = path.replace(":workspaceId", groupId);
          // The assistant editor lives at /assistant/:id; the model sometimes
          // guesses the nonexistent legacy /agents/:id path (DEV-1513).
          path = path.replace(/^\/agents\//, "/assistant/");
          const error = navigationError(path, pages);
          if (error) return { error };
          return navigateAndSettle((to) => router.push(to), path);
        },
        // Say WHERE it went - "Navigate To" with a wrench tells the user nothing.
        display: {
          icon: Compass,
          action: "Go to this page",
          grant: "Moving between pages",
          approval: {
            op: "go",
            entity: "page",
            name_arg: "page",
            fields: [{ arg: "path", type: "text", label: "Address" }],
          },
          messageStart: (input) =>
            typeof input.page === "string"
              ? `Opening ${input.page}...`
              : "Navigating...",
          messageEnd: (input) =>
            typeof input.page === "string"
              ? `Opened ${input.page}`
              : "Navigated",
          messageError: "Navigation failed",
        },
      },
      {
        name: "search_pages",
        description:
          "Search available platform pages by keyword. Returns paths and descriptions. Use this before navigate_to to find the correct path.",
        parameters: {
          type: "object",
          properties: {
            query: {
              type: "string",
              description:
                "Search query (e.g. 'api keys', 'billing', 'workflow editor')",
            },
          },
          required: ["query"],
        },
        execute: async (input) => {
          const query = (input.query as string).toLowerCase();
          const tokens = query.split(/\s+/).filter(Boolean);
          const matches = pages
            .filter((page) => {
              const haystack = [
                page.path,
                page.description,
                ...(page.tags ?? []),
              ]
                .join(" ")
                .toLowerCase();
              return tokens.every((t) => haystack.includes(t));
            })
            .map(({ path, description }) => ({ path, description }));
          return { results: matches, count: matches.length };
        },
        display: {
          icon: Search,
          messageStart: (input) =>
            typeof input.query === "string"
              ? `Searching pages for "${input.query}"...`
              : "Searching pages...",
          messageEnd: () => "Searched pages",
          messageError: "Page search failed",
        },
      },
      {
        name: "show_notification",
        description: "Show a toast notification to the user",
        parameters: {
          type: "object",
          properties: {
            message: {
              type: "string",
              description: "The notification message",
            },
            type: {
              type: "string",
              enum: ["success", "error", "info"],
              description: "The notification type (defaults to info)",
            },
          },
          required: ["message"],
        },
        execute: async (input) => {
          const message = input.message as string;
          const type = (input.type as string) || "info";
          if (type === "success") SpotToast.success(message);
          else if (type === "error") SpotToast.error(message);
          else SpotToast.info(message);
          return { shown: true };
        },
        display: {
          icon: Bell,
          messageStart: () => "Showing a notification...",
          messageEnd: () => "Showed a notification",
          messageError: "Notification failed",
        },
      },
      {
        name: "highlight_element",
        description:
          "Point the user at one element on the page: a ring around it with your note beside it, until they click anywhere. It never clicks or changes anything. Pass the element's id and its label exactly as get_page_elements shows them.",
        parameters: {
          type: "object",
          properties: {
            element_id: {
              type: "string",
              description: "The element's id from get_page_elements (ge-12)",
            },
            label: {
              type: "string",
              description:
                "The element's label as get_page_elements shows it (More row options)",
            },
            note: {
              type: "string",
              description:
                "One short sentence shown beside it (Open this, then Import Rows from CSV)",
            },
          },
          required: ["element_id", "label", "note"],
        },
        execute: async (input) =>
          highlightElement(
            input.element_id as string,
            input.label as string,
            input.note as string
          ),
        display: {
          icon: Crosshair,
          messageStart: (input) =>
            typeof input.label === "string"
              ? `Pointing at ${input.label}...`
              : "Pointing...",
          messageEnd: (input) =>
            typeof input.label === "string"
              ? `Pointed at ${input.label}`
              : "Pointed at an element",
          messageError: "Could not point at it",
        },
      },
      {
        name: "get_page_elements",
        description:
          "Return the page the user is on now as simplified HTML. Call it before you name a button, menu or field to the user, so you use the page's own labels.",
        parameters: { type: "object", properties: {} },
        execute: async () => ({ html: takeSnapshot().html }),
        display: {
          icon: ScanSearch,
          messageStart: () => "Reading the page...",
          messageEnd: () => "Read the page",
          messageError: "Could not read the page",
        },
      },
    ];

    return tools;
  }, [router, groupId, editorV2Enabled]);
}
